import counterpart from 'counterpart';
import moment from 'moment';
import React, { FormEvent, useState } from 'react';
import { Col, Row } from 'react-grid-system';
import FormGroup from '../../components/ui/form-group/FormGroup';
import FormLabel from '../../components/ui/form-label/FormLabel';
import Input from '../../components/ui/input/Input';
import PageWrapper from '../../components/ui/layout/PageWrapper';
import Panel from '../../components/ui/panel/Panel';
import DateTimePicker from '../../components/ui/date-time-picker/DateTimePicker';
import Button from '../../components/ui/button/Button';
import NoticePanel from '../../components/ui/notice-panel/NoticePanel';
import { UpdateSubscriptionBillingDateRequest } from '../../utils/grpc/generated/Billsby.Protos/core/private/subscriptions/subscriptions_pb';
import { SubscriptionsServiceClient } from '../../utils/grpc/generated/Billsby.Protos/core/private/subscriptions/SubscriptionsServiceClientPb';
import { ConfigConstants } from '../../utils/config';
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb';
import { grpcUnaryCall } from '../../utils/grpc/grpcUtils';
import { allowZeroAndPositiveNumbers, extractGrpcErrorMessage } from '../../utils/commonUtils';
import Text from '../../components/ui/text/Text';
import { Link } from 'react-router-dom';
import API from '../../utils/API';

interface IChangeCancellationDate {
  newCancellationDate?: Date,
  companyId?: number,
  isLoading: boolean
  isSuccess: boolean,
  apiError: string
}

const ChangeCancellationDate: React.FC = () => {
  const dateFormat = "MM/dd/yyyy h:mm aa";
  const [state, setState] = useState<IChangeCancellationDate>({
    newCancellationDate: undefined,
    isLoading: false,
    isSuccess: false,
    apiError: ""
  })

  const getErrorMsg = (code?: string) => {
    switch (code) {
      case "Company_Update_Deletion_Date_Company_NotFound":
        return counterpart("CHANGE_CANCELLATION_DATE_ERROR1")
      case "Company_Update_Deletion_Date_No_Active_Request":
        return counterpart("CHANGE_CANCELLATION_DATE_ERROR2")
      default:
        return counterpart("CHANGE_CANCELLATION_DATE_ERROR3")
    }
  }

  const onSubmit = async (evt: FormEvent) => {
    evt.preventDefault();
    if (!companyId) {
      return;
    }
    setState((prevState: IChangeCancellationDate) => ({ ...prevState, isLoading: true, isSuccess: false, apiError: "" }));
    try {
      await API.updateCancellationDate(companyId, moment(newCancellationDate, dateFormat).utc().toDate())
      setState((prevState: IChangeCancellationDate) => ({ ...prevState, isLoading: false, isSuccess: true, apiError: "" }));
    }
    catch (err) {
      const { data: { errors } } = err as any;
      setState((prevState: IChangeCancellationDate) => ({ ...prevState, isLoading: false, isSuccess: false, apiError: getErrorMsg(errors[0].code) }));
    }
  }

  const { newCancellationDate, companyId, isLoading, isSuccess, apiError } = state;

  return (
    <PageWrapper className="change-billing-date">

      <Panel title='CHANGE_CANCELLATION_DATE_TITLE'>
        <Text content='CHANGE_CANCELLATION_DATE_SUBTITLE' translateWith={{
          link: <Link to="/companies">{counterpart("NAVBAR_COMPANIES").toLowerCase()}</Link>
        }} noMargin></Text>
      </Panel>

      {isSuccess && !isLoading &&
        <NoticePanel
          type="success"
          icon="far fa-check-circle"
          content="CHANGE_CANCELLATION_DATE_SUCCESS"
        />
      }

      {!!apiError && !isLoading &&
        <NoticePanel
          type="error"
          icon="far fa-exclamation-circle"
          content={apiError || counterpart("CHANGE_BILLING_DATE_SUBSCRIPTION_ERROR")}
          shouldTranslate={false}
        />
      }

      <Panel>
        <form onSubmit={onSubmit}>
          <FormGroup>
            <Row align="center">
              <Col xs={2}>
                <FormLabel
                  target="company-id"
                  content="CHANGE_CANCELLATION_DATE_COMPANY_ID"
                  noMargin
                />
              </Col>
              <Col xs={10}>
                <Input
                  id="company-id"
                  required
                  value={companyId}
                  placeholder={counterpart("CHANGE_CANCELLATION_DATE_COMPANY_ID")}
                  onChange={(evt: any) => setState({ ...state, companyId: allowZeroAndPositiveNumbers(evt.target.value) })}
                />
              </Col>
            </Row>
          </FormGroup>

          <FormGroup>
            <Row align="center">
              <Col xs={2}>
                <FormLabel
                  target="new-cancellation-date"
                  content="CHANGE_CANCELLATION_DATE_CANCELLATION_DATE"
                  noMargin
                />
              </Col>
              <Col xs={10}>
                <DateTimePicker
                  value={newCancellationDate}
                  onChange={value => {
                    setState({ ...state, newCancellationDate: value });
                  }} />
              </Col>
            </Row>
          </FormGroup>

          <Button
            id="send-request"
            type="submit"
            disabled={isLoading || !companyId || !newCancellationDate}
            isLoading={isLoading}
            title="CHANGE_CANCELLATION_DATE_BUTTON"
            isFullWidth
          />
        </form>
      </Panel>

    </PageWrapper>
  )
}

export default ChangeCancellationDate;